/**
 * Typed settings registry. Values are stored as JSON in `business_setting` / `location_setting`,
 * but every key has a definition here and in `setting_definition` (SQL), so the server rejects
 * values that do not match. Effective value = location override ?? business value ?? default.
 */
export type SettingScope = 'business' | 'location' | 'both';
export type SettingValueType = 'boolean' | 'integer' | 'string' | 'enum' | 'time';

export interface SettingDefinition {
  key: SettingKey;
  /** The setting's name on screen; the key stays for code, tests and the audit log. */
  label: string;
  scope: SettingScope;
  type: SettingValueType;
  default: boolean | number | string;
  description: string;
  /** Operationally meaningful settings are always audited on change. */
  audited: boolean;
  min?: number;
  max?: number;
  enumValues?: readonly string[];
  /** The value is an amount of money in minor units (centavos); the UI edits it in pesos. */
  money?: boolean;
  /** ADR-031: changed by the business owner only, signed in on their own account. Mirrors setting_definition.owner_only. */
  ownerOnly?: true;
}

export const SETTING_KEYS = [
  'orders.auto_send_after_payment',
  'orders.require_payment_before_send',
  'payments.allow_partial_on_regular_orders',
  'kds.warn_seconds',
  'kds.late_seconds',
  'kds.undo_window_seconds',
  'kds.chime_new_ticket',
  'serve.undo_window_seconds',
  'expo.warn_seconds',
  'expo.late_seconds',
  'business.day_start_time',
  'business.test_fixture',
  'security.pin_max_attempts',
  'security.pin_lockout_seconds',
  'security.approval_grant_seconds',
  'terminal.idle_lock_seconds.pos',
  'terminal.idle_lock_seconds.kds',
  'terminal.idle_lock_seconds.expo',
  'terminal.idle_lock_seconds.admin',
  'terminal.heartbeat_seconds',
  'terminal.online_threshold_seconds',
  'cash.safe_enabled',
  'cash.standard_float_minor',
  'cash.count_tolerance_minor',
  'cash.payout_review_minor',
  'shift.takeover_idle_minutes',
  'shift.max_open_hours',
  'shift.closing_max_minutes',
  'shift.late_entry_hours',
  'cash.reconcile_due_hours',
  'review.shortfall_week_minor',
  'review.reconcile_from',
  'review.alert_no_sale_minutes',
  'review.alert_near_limit_percent',
  'review.alert_near_limit_count',
  'security.owner_reauth_seconds',
  'writeoff.walkout_max_minor',
  'writeoff.daily_max_minor',
  'writeoff.daily_max_count',
  'writeoff.staff_meal_daily_minor',
  'refund.cashier_allowance',
  'refund.cashier_order_max_minor',
  'refund.cashier_daily_max_minor',
] as const;

export type SettingKey = (typeof SETTING_KEYS)[number];

export const SETTING_DEFINITIONS: readonly SettingDefinition[] = [
  {
    key: 'orders.auto_send_after_payment',
    label: 'Send paid orders automatically',
    scope: 'both',
    type: 'boolean',
    default: false,
    audited: true,
    description:
      'On: a fully paid order goes to the kitchen and bar by itself. Off: the cashier presses Send order.',
  },
  {
    key: 'orders.require_payment_before_send',
    label: 'Pay before sending',
    scope: 'both',
    type: 'boolean',
    default: true,
    audited: true,
    description:
      'On (pay first): an order is paid before it goes to the kitchen and bar, unless the employee’s role may send unpaid orders.',
  },
  {
    key: 'payments.allow_partial_on_regular_orders',
    label: 'Part payments on regular orders',
    scope: 'both',
    type: 'boolean',
    default: false,
    audited: true,
    description:
      'On: a payment may cover only part of a regular order. Off (the default): only advance orders take deposits; every other order is paid in full.',
  },
  {
    key: 'kds.warn_seconds',
    label: 'Ticket turns amber after (seconds)',
    scope: 'both',
    type: 'integer',
    default: 300,
    min: 30,
    max: 7200,
    audited: true,
    description:
      'Seconds after send at which a ticket turns amber on the Kitchen and Bar displays (stations may override).',
  },
  {
    key: 'kds.late_seconds',
    label: 'Ticket turns red after (seconds)',
    scope: 'both',
    type: 'integer',
    default: 600,
    min: 60,
    max: 14400,
    audited: true,
    description:
      'Seconds after send at which a ticket turns red on the Kitchen and Bar displays (stations may override).',
  },
  {
    key: 'kds.undo_window_seconds',
    label: 'Undo after a bump (seconds)',
    scope: 'both',
    type: 'integer',
    default: 120,
    min: 10,
    max: 3600,
    audited: true,
    description:
      'Seconds after a bump during which a ticket or item can be undone without a reason; later it needs a recall with a reason.',
  },
  {
    key: 'kds.chime_new_ticket',
    label: 'Chime for new tickets',
    scope: 'both',
    type: 'boolean',
    default: true,
    audited: false,
    description:
      'Play a chime on the display when a new ticket arrives (a distinct alert always plays for cancellations when sound is on).',
  },
  {
    key: 'serve.undo_window_seconds',
    label: 'Undo after a serve (seconds)',
    scope: 'both',
    type: 'integer',
    default: 120,
    min: 10,
    max: 3600,
    audited: true,
    description:
      'Seconds after a serve during which it can be undone without a reason; after that a correction needs a reason and someone allowed to correct serves (or their PIN).',
  },
  {
    key: 'expo.warn_seconds',
    label: 'Waiting at the pass, amber after (seconds)',
    scope: 'both',
    type: 'integer',
    default: 120,
    min: 30,
    max: 7200,
    audited: true,
    description: 'Seconds a ready order may wait at the pass before its Expo card turns amber.',
  },
  {
    key: 'expo.late_seconds',
    label: 'Waiting at the pass, red after (seconds)',
    scope: 'both',
    type: 'integer',
    default: 300,
    min: 60,
    max: 14400,
    audited: true,
    description: 'Seconds a ready order may wait at the pass before its Expo card turns red.',
  },
  {
    key: 'business.day_start_time',
    label: 'Business day starts at',
    scope: 'both',
    type: 'time',
    default: '04:00',
    audited: true,
    description:
      'Local time at which the business day rolls over (used for business dates, order numbering and reports).',
  },
  {
    key: 'business.test_fixture',
    label: 'Test business',
    scope: 'business',
    type: 'boolean',
    default: false,
    audited: true,
    description: 'On: a test business; reports leave it out. Never on for the café.',
  },
  {
    key: 'security.pin_max_attempts',
    label: 'PIN attempts before a lockout',
    scope: 'business',
    type: 'integer',
    default: 5,
    min: 3,
    max: 10,
    audited: true,
    description:
      'Failed PIN attempts allowed within the lockout window before an employee is locked out on that terminal.',
  },
  {
    key: 'security.pin_lockout_seconds',
    label: 'PIN lockout (seconds)',
    scope: 'business',
    type: 'integer',
    default: 900,
    min: 60,
    max: 3600,
    audited: true,
    description: 'Lockout window and duration in seconds after too many failed PIN attempts.',
  },
  {
    key: 'security.approval_grant_seconds',
    label: 'An approval stays usable for (seconds)',
    scope: 'business',
    type: 'integer',
    default: 120,
    min: 30,
    max: 900,
    audited: true,
    description:
      'Seconds an approval stays usable after the approver confirms it with their PIN; the approved action must be sent within it.',
  },
  {
    key: 'terminal.idle_lock_seconds.pos',
    label: 'POS tablets',
    scope: 'both',
    type: 'integer',
    default: 600,
    min: 60,
    max: 3600,
    audited: true,
    description: 'Idle seconds before a POS tablet locks and asks for a PIN again.',
  },
  {
    key: 'terminal.idle_lock_seconds.kds',
    label: 'Kitchen and bar screens',
    scope: 'both',
    type: 'integer',
    default: 43200,
    min: 300,
    max: 86400,
    audited: true,
    description: 'Idle seconds before a kitchen or bar screen locks.',
  },
  {
    key: 'terminal.idle_lock_seconds.expo',
    label: 'Expo screens',
    scope: 'both',
    type: 'integer',
    default: 1800,
    min: 60,
    max: 86400,
    audited: true,
    description: 'Idle seconds before an Expo screen locks.',
  },
  {
    key: 'terminal.idle_lock_seconds.admin',
    label: 'Office devices',
    scope: 'both',
    type: 'integer',
    default: 1800,
    min: 60,
    max: 86400,
    audited: true,
    description: 'Idle seconds before an office device locks.',
  },
  {
    key: 'terminal.heartbeat_seconds',
    label: 'Devices check in every (seconds)',
    scope: 'business',
    type: 'integer',
    default: 60,
    min: 15,
    max: 300,
    audited: false,
    description: 'How often each enrolled device tells the system it is on.',
  },
  {
    key: 'terminal.online_threshold_seconds',
    label: 'Shown offline after (seconds)',
    scope: 'business',
    type: 'integer',
    default: 150,
    min: 30,
    max: 900,
    audited: false,
    description: 'A device that has not checked in for this long is shown as offline.',
  },
  /* ── shifts and cash (Phase 7A, ADR-024) ── */
  {
    key: 'cash.safe_enabled',
    label: 'Safe',
    scope: 'both',
    type: 'boolean',
    default: false,
    audited: true,
    description:
      'On: the location has a safe; cashiers drop cash into numbered envelopes and the cash removed at a close goes into one. Off: no safe, and cash leaves the drawer only into the owner’s hands.',
  },
  {
    key: 'cash.standard_float_minor',
    label: 'Standard float',
    scope: 'both',
    type: 'integer',
    default: 0,
    min: 0,
    max: 10_000_000,
    money: true,
    audited: true,
    description:
      'Cash suggested to keep in the drawer for the next shift. 0 (unset): keep what was counted.',
  },
  {
    key: 'cash.count_tolerance_minor',
    label: 'Count tolerance',
    scope: 'both',
    type: 'integer',
    default: 5_000,
    min: 0,
    max: 100_000,
    money: true,
    audited: true,
    description:
      'A drawer count within this amount of the expected cash needs no recount and no explanation.',
  },
  {
    key: 'cash.payout_review_minor',
    label: 'Pay-outs flagged above',
    scope: 'both',
    type: 'integer',
    default: 50_000,
    min: 0,
    max: 10_000_000,
    money: true,
    audited: true,
    description:
      'Pay-outs above this amount (one pay-out, one cashier’s day or one payee’s day) are flagged for the owner. They are never refused.',
  },
  {
    key: 'shift.takeover_idle_minutes',
    label: 'Takeover after inactivity (minutes)',
    scope: 'both',
    type: 'integer',
    default: 60,
    min: 15,
    max: 1440,
    audited: true,
    description:
      'Minutes without activity by a shift’s cashier after which another cashier may take the shift over without the owner.',
  },
  {
    key: 'shift.max_open_hours',
    label: 'Shift flagged after (hours)',
    scope: 'both',
    type: 'integer',
    default: 16,
    min: 4,
    max: 48,
    audited: true,
    description: 'A shift open longer than this many hours is flagged.',
  },
  {
    key: 'shift.closing_max_minutes',
    label: 'Drawer count flagged after (minutes)',
    scope: 'both',
    type: 'integer',
    default: 30,
    min: 5,
    max: 240,
    audited: true,
    description: 'A drawer count left unfinished longer than this many minutes is flagged.',
  },
  {
    key: 'shift.late_entry_hours',
    label: 'Late payments accepted for (hours)',
    scope: 'both',
    type: 'integer',
    default: 24,
    min: 1,
    max: 72,
    audited: true,
    description:
      'Hours after a shift starts closing during which a payment taken offline on its drawer can still be added to it as a late entry.',
  },
  /* ── the owner's review (Phase 7A-2A, ADR-026) ── */
  {
    key: 'cash.reconcile_due_hours',
    label: 'Check non-cash payments within (hours)',
    scope: 'both',
    type: 'integer',
    default: 48,
    min: 1,
    max: 168,
    audited: true,
    description:
      'Hours after a non-cash payment (GCash and the like) is recorded within which the owner checks it against the provider’s record; past that it is listed to check.',
  },
  {
    key: 'review.shortfall_week_minor',
    label: 'Small shortfalls over 7 days, flagged above',
    scope: 'both',
    type: 'integer',
    default: 30_000,
    min: 0,
    max: 10_000_000,
    money: true,
    audited: true,
    description:
      'Within-tolerance shortfalls by one person over 7 days beyond this amount are listed for the owner.',
  },
  {
    key: 'review.reconcile_from',
    label: 'Check non-cash payments from',
    scope: 'business',
    type: 'string',
    default: '',
    audited: true,
    description:
      'The business date (YYYY-MM-DD) from which non-cash payments are due for checking. Earlier ones are listed, never flagged. Set when the owner review started.',
  },
  /* ── the owner review's alerts (ADR-034): the owner's thresholds ── */
  {
    key: 'review.alert_no_sale_minutes',
    label: 'Cash given back near a no-sale (minutes)',
    scope: 'both',
    type: 'integer',
    default: 10,
    min: 1,
    max: 120,
    audited: true,
    ownerOnly: true,
    description:
      'Cash given back and a no-sale on the same shift within this many minutes of each other are listed for the owner.',
  },
  {
    key: 'review.alert_near_limit_percent',
    label: 'Just under the limit (percent)',
    scope: 'both',
    type: 'integer',
    default: 90,
    min: 50,
    max: 100,
    audited: true,
    ownerOnly: true,
    description:
      'A walk-out or return made alone at or above this percent of the per-event limit counts as just under the limit.',
  },
  {
    key: 'review.alert_near_limit_count',
    label: 'Just under the limit, times a day',
    scope: 'both',
    type: 'integer',
    default: 2,
    min: 2,
    max: 20,
    audited: true,
    ownerOnly: true,
    description:
      'This many walk-outs or returns just under the limit by one person in one business day are listed for the owner.',
  },
  {
    key: 'security.owner_reauth_seconds',
    label: 'Owner verifies again after (seconds)',
    scope: 'business',
    type: 'integer',
    default: 900,
    min: 300,
    max: 3600,
    audited: true,
    description:
      'Seconds after the owner signed in or verified their authenticator during which review actions that clear an item are accepted; after that they verify again.',
  },
  /* ── write-offs (ADR-031): the owner's limits, D1 and D3 ── */
  {
    key: 'writeoff.walkout_max_minor',
    label: 'Largest walk-out without the owner',
    scope: 'both',
    type: 'integer',
    default: 50_000,
    min: 0,
    max: 10_000_000,
    money: true,
    audited: true,
    ownerOnly: true,
    description: 'The largest walk-out a cashier writes off alone; above it the owner approves.',
  },
  {
    key: 'writeoff.daily_max_minor',
    label: 'Walk-outs a day, total amount',
    scope: 'both',
    type: 'integer',
    default: 100_000,
    min: 0,
    max: 10_000_000,
    money: true,
    audited: true,
    ownerOnly: true,
    description:
      'All walk-outs written off at a location in one business day; beyond it every walk-out needs the owner. A new shift never resets it.',
  },
  {
    key: 'writeoff.daily_max_count',
    label: 'Walk-outs a day, number',
    scope: 'both',
    type: 'integer',
    default: 2,
    min: 0,
    max: 50,
    audited: true,
    ownerOnly: true,
    description:
      'Walk-outs cashiers write off alone at a location in one business day; beyond it the owner approves.',
  },
  {
    key: 'writeoff.staff_meal_daily_minor',
    label: 'Staff meals a person a day',
    scope: 'both',
    type: 'integer',
    default: 30_000,
    min: 0,
    max: 10_000_000,
    money: true,
    audited: true,
    ownerOnly: true,
    description:
      'Staff meals written off for one employee in one business day without the owner, their own included.',
  },
  /* ── the cashier's allowance for money given back (ADR-035): ships off ── */
  {
    key: 'refund.cashier_allowance',
    label: 'Cashiers give money back without the owner',
    scope: 'both',
    type: 'boolean',
    default: false,
    audited: true,
    ownerOnly: true,
    description:
      'On: an employee whose role may give money back alone (allow it for the Cashier role under Roles & permissions) gives money back, a refund or a payment void, without the owner: within the two limits below, the way it was paid, on an order where nothing else happened. Off: every refund and payment void needs the owner.',
  },
  {
    key: 'refund.cashier_order_max_minor',
    label: 'Most given back on one order',
    scope: 'both',
    type: 'integer',
    default: 20_000,
    min: 0,
    max: 10_000_000,
    money: true,
    audited: true,
    ownerOnly: true,
    description:
      'Most the cashiers give back without the owner on one order, refunds and payment voids together.',
  },
  {
    key: 'refund.cashier_daily_max_minor',
    label: 'Most one cashier gives back a day',
    scope: 'both',
    type: 'integer',
    default: 50_000,
    min: 0,
    max: 10_000_000,
    money: true,
    audited: true,
    ownerOnly: true,
    description:
      'Most one cashier gives back without the owner in a business day, across shifts: closing and reopening a shift does not reset it.',
  },
];

/** Cash, shift, review, write-off and refund settings are changed from the owner's signed-in account, never on a tablet (ADR-024, ADR-026, ADR-031, ADR-035). */
export function isOwnerAccountSetting(key: string): boolean {
  return /^(cash|shift|review|writeoff|refund)\./.test(key);
}

/** ADR-031, ADR-034, ADR-035: settings no one but the business owner changes (the write-off limits, the review alerts, the cashier's allowance). */
export function isOwnerOnlySetting(key: string): boolean {
  return BY_KEY.get(key)?.ownerOnly === true;
}

const BY_KEY = new Map<string, SettingDefinition>(SETTING_DEFINITIONS.map((d) => [d.key, d]));

export function settingDefinition(key: string): SettingDefinition | undefined {
  return BY_KEY.get(key);
}

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export type ValidationResult = { ok: true } | { ok: false; error: string };

export function validateSettingValue(key: string, value: unknown): ValidationResult {
  const def = BY_KEY.get(key);
  if (!def) return { ok: false, error: `Unknown setting "${key}"` };
  switch (def.type) {
    case 'boolean':
      return typeof value === 'boolean'
        ? { ok: true }
        : { ok: false, error: 'Expected true or false' };
    case 'integer': {
      if (typeof value !== 'number' || !Number.isInteger(value))
        return { ok: false, error: 'Expected a whole number' };
      if (def.min !== undefined && value < def.min)
        return { ok: false, error: `Minimum is ${def.min}` };
      if (def.max !== undefined && value > def.max)
        return { ok: false, error: `Maximum is ${def.max}` };
      return { ok: true };
    }
    case 'string':
      return typeof value === 'string' ? { ok: true } : { ok: false, error: 'Expected text' };
    case 'enum':
      return typeof value === 'string' && (def.enumValues ?? []).includes(value)
        ? { ok: true }
        : { ok: false, error: `Expected one of: ${(def.enumValues ?? []).join(', ')}` };
    case 'time':
      return typeof value === 'string' && TIME_RE.test(value)
        ? { ok: true }
        : { ok: false, error: 'Expected a time in HH:MM (24-hour) format' };
  }
}

export type SettingValue = boolean | number | string;

/** Effective value resolution: location override, then business value, then the definition default. */
export function resolveSetting(
  key: SettingKey,
  businessValues: Readonly<Record<string, SettingValue | undefined>>,
  locationValues: Readonly<Record<string, SettingValue | undefined>> = {},
): SettingValue {
  const def = BY_KEY.get(key);
  if (!def) throw new Error(`Unknown setting "${key}"`);
  if (def.scope !== 'business' && locationValues[key] !== undefined)
    return locationValues[key] as SettingValue;
  if (def.scope !== 'location' && businessValues[key] !== undefined)
    return businessValues[key] as SettingValue;
  return def.default;
}
